/**
 * React Query Hooks for Contracts
 *
 * Provides data fetching and caching for contract operations using TanStack Query.
 * Implements automatic caching, background refetching, and optimistic updates.
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import {
  contractService,
  ContractApiResponse,
  RepositoryContractFilters,
  RepositoryContractPage,
  NewRepositoryContract,
  SupplierCoverageFilters,
  SupplierCoverageReport
} from '../../services/api/contractService';
import { queryKeys, STALE_TIMES } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Contract filter parameters
 */
export interface ContractFilters {
  status?: string;
  reviewStatus?: string;
  supplierId?: string;
  year?: number;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

/**
 * Hook to fetch all contracts with optional filters
 * @param filters - Optional filtering and pagination parameters
 * @param options - Additional query options
 */
export function useContracts(
  filters: ContractFilters = {},
  options?: Omit<UseQueryOptions<ContractApiResponse[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.contracts.list(filters as Record<string, unknown>),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractService.getAll(token, filters);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.CONTRACTS,
    ...options,
  });
}

/**
 * Hook to fetch a single contract by ID
 * @param contractId - The contract ID to fetch
 * @param options - Additional query options
 */
export function useContract(
  contractId: string | undefined,
  options?: Omit<UseQueryOptions<ContractApiResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.contracts.detail(contractId || ''),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!contractId) throw new Error('Contract ID required');
      return contractService.getById(token, contractId);
    },
    enabled: !!token && !!contractId,
    staleTime: STALE_TIMES.CONTRACT_DETAIL,
    ...options,
  });
}

/**
 * Hook to create a new contract
 */
export function useCreateContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractData: Partial<ContractApiResponse>) => {
      if (!token) throw new Error('No authentication token');
      return contractService.create(token, contractData);
    },
    onSuccess: (newContract) => {
      // Invalidate contract list queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
      // Add the new contract to cache
      queryClient.setQueryData(
        queryKeys.contracts.detail(newContract._id || newContract.id?.toString() || ''),
        newContract
      );
    },
  });
}

/**
 * Hook to update an existing contract
 */
export function useUpdateContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ContractApiResponse> }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.update(token, id, updates);
    },
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.contracts.detail(id) });

      // Snapshot the previous value
      const previousContract = queryClient.getQueryData<ContractApiResponse>(
        queryKeys.contracts.detail(id)
      );

      // Optimistically update to the new value
      if (previousContract) {
        queryClient.setQueryData(queryKeys.contracts.detail(id), {
          ...previousContract,
          ...updates,
        });
      }

      return { previousContract };
    },
    onError: (err, { id }, context) => {
      // Rollback on error
      if (context?.previousContract) {
        queryClient.setQueryData(queryKeys.contracts.detail(id), context.previousContract);
      }
    },
    onSettled: (data, error, { id }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    },
  });
}

/**
 * Hook to delete a contract
 */
export function useDeleteContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractId: string) => {
      if (!token) throw new Error('No authentication token');
      return contractService.delete(token, contractId);
    },
    onSuccess: (_, contractId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.contracts.detail(contractId) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    },
  });
}

/**
 * Hook to bulk delete contracts
 */
export function useBulkDeleteContracts() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractIds: string[]) => {
      if (!token) throw new Error('No authentication token');
      return contractService.bulkDelete(token, contractIds);
    },
    onSuccess: (_, contractIds) => {
      // Remove all deleted contracts from cache
      contractIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: queryKeys.contracts.detail(id) });
      });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    },
  });
}

/**
 * Hook to fetch contract analysis
 */
export function useContractAnalysis(
  contractId: string | undefined,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.contracts.detail(contractId || ''), 'analysis'],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!contractId) throw new Error('Contract ID required');
      return contractService.getAnalysis(token, contractId);
    },
    enabled: !!token && !!contractId,
    staleTime: STALE_TIMES.CONTRACT_DETAIL,
    ...options,
  });
}

/**
 * Hook to run AI analysis on a contract
 */
export function useRunContractAnalysis() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contractId,
      onProgress,
    }: {
      contractId: string;
      onProgress?: (progress: any) => void;
    }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.runAIAnalysis(token, contractId, onProgress);
    },
    onSuccess: (_, { contractId }) => {
      // Invalidate analysis cache
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.detail(contractId), 'analysis'],
      });
      // Also invalidate the contract detail to get updated analysis status
      queryClient.invalidateQueries({
        queryKey: queryKeys.contracts.detail(contractId),
      });
    },
  });
}

/**
 * Hook to fetch contract files
 */
export function useContractFiles(
  contractId: string | undefined,
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.contracts.detail(contractId || ''), 'files'],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!contractId) throw new Error('Contract ID required');
      return contractService.getFiles(token, contractId);
    },
    enabled: !!token && !!contractId,
    staleTime: STALE_TIMES.CONTRACTS,
    ...options,
  });
}

/**
 * Hook to fetch one page of the contract repository (signed contracts)
 * @param filters - Search, filter, sort and paging
 */
export function useRepositoryContracts(filters: RepositoryContractFilters = {}) {
  const { token } = useAuth();

  return useQuery<RepositoryContractPage, Error>({
    queryKey: queryKeys.contracts.repository(filters as Record<string, unknown>),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractService.getRepositoryContracts(token, filters);
    },
    enabled: !!token,
    // Contracts are edited from the Contracts tab without going through these
    // hooks, so refetch whenever the repository is opened.
    staleTime: 0,
    // Keep the current page on screen while the next search/page loads.
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch which active suppliers have a valid signed contract
 */
export function useSupplierCoverage(filters: SupplierCoverageFilters = {}) {
  const { token } = useAuth();

  return useQuery<SupplierCoverageReport, Error>({
    queryKey: [...queryKeys.contracts.all, 'coverage', filters],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractService.getSupplierCoverage(token, filters);
    },
    enabled: !!token,
    // Same as the repository: contracts change elsewhere, so refetch on open.
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to delete a contract from the repository. mode 'remove' only takes it
 * out of the repository (retires its signed documents); 'delete' deletes the
 * contract and all its documents.
 */
export function useDeleteRepositoryContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractId, mode }: { contractId: string; mode: 'remove' | 'delete' }) => {
      if (!token) throw new Error('No authentication token');
      if (mode === 'remove') {
        await contractService.removeFromRepository(token, contractId);
      } else {
        await contractService.delete(token, contractId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
    },
  });
}

/**
 * Hook to read a signed contract for suggested details (nothing is stored)
 */
export function useExtractRepositoryContract() {
  const { token } = useAuth();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error('No authentication token');
      return contractService.extractRepositoryContract(token, file);
    },
  });
}

/**
 * Hook to add a signed contract to the repository (contract + signed document)
 */
export function useCreateRepositoryContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, details }: { file: File; details: NewRepositoryContract }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.createRepositoryContract(token, file, details);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
    },
  });
}

/**
 * Hook to upload a file to a contract
 */
export function useUploadContractFile() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contractId,
      file,
      metadata,
    }: {
      contractId: string;
      file: File;
      metadata?: Record<string, any>;
    }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.uploadFile(token, contractId, file, metadata);
    },
    onSuccess: (_, { contractId }) => {
      // Invalidate files cache
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.detail(contractId), 'files'],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.all, 'repository'],
      });
    },
  });
}

/**
 * Hook to delete a contract file
 */
export function useDeleteContractFile() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractId, fileId }: { contractId: string; fileId: string }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.deleteFile(token, contractId, fileId);
    },
    onSuccess: (_, { contractId }) => {
      // Invalidate files cache
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.detail(contractId), 'files'],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.all, 'repository'],
      });
    },
  });
}

/**
 * Hook to fetch contract notes
 */
export function useContractNotes(
  contractId: string | undefined,
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.contracts.detail(contractId || ''), 'notes'],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!contractId) throw new Error('Contract ID required');
      return contractService.getNotes(token, contractId);
    },
    enabled: !!token && !!contractId,
    staleTime: STALE_TIMES.CONTRACTS,
    ...options,
  });
}

/**
 * Hook to add a note to a contract
 */
export function useAddContractNote() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contractId, noteData }: { contractId: string; noteData: any }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.addNote(token, contractId, noteData);
    },
    onSuccess: (_, { contractId }) => {
      // Invalidate notes cache
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.detail(contractId), 'notes'],
      });
    },
  });
}

/**
 * Hook to update clause decisions
 */
export function useUpdateClauseDecision() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contractId,
      analysisId,
      clauseRef,
      status,
      notes,
    }: {
      contractId: string;
      analysisId: string;
      clauseRef: string;
      status: string;
      notes?: string;
    }) => {
      if (!token) throw new Error('No authentication token');
      return contractService.updateClauseDecision(token, contractId, analysisId, clauseRef, status, notes);
    },
    onSuccess: (_, { contractId }) => {
      // Invalidate analysis cache
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.contracts.detail(contractId), 'analysis'],
      });
    },
  });
}

/**
 * Hook to generate negotiation email
 */
export function useGenerateNegotiationEmail() {
  const { token } = useAuth();

  return useMutation({
    mutationFn: async (emailData: any) => {
      if (!token) throw new Error('No authentication token');
      return contractService.generateNegotiationEmail(token, emailData);
    },
  });
}

/**
 * Hook to prefetch contract data
 */
export function usePrefetchContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return async (contractId: string) => {
    if (!token) return;

    await queryClient.prefetchQuery({
      queryKey: queryKeys.contracts.detail(contractId),
      queryFn: () => contractService.getById(token, contractId),
      staleTime: STALE_TIMES.CONTRACT_DETAIL,
    });
  };
}

/**
 * Hook to invalidate all contract queries
 * Useful after bulk operations or when data needs to be refreshed
 */
export function useInvalidateContracts() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
  };
}
